import axios from 'axios';

import { CONFIG } from '@/config/config.ts';

type Token = {
  headerName: 'X-CSRF-TOKEN';
  parameterName: '_csrf';
  token: string;
};

// Backend uses HttpSessionCsrfTokenRepository — the CSRF token is STABLE for the lifetime of
// the session and does not rotate per request. Re-fetching it before every mutating request
// (the previous behaviour) doubled the write-path round-trips and, in a captured real exam,
// accounted for ~27% of total server time doing nothing. So we fetch it once and reuse it.
//
// Correctness: the only events that change the token are session boundaries (login / logout /
// a session reset). Those self-heal — a stale token yields a 403, and invalidateCsrfToken()
// is called from the 403 path (see httpErrorHandler), so the very next request refetches.
let cachedToken: string | null = null;
// Dedupe concurrent first-fetches: if several mutating requests fire before the first token
// arrives, they all await the same in-flight request instead of each hitting /csrf.
let inFlight: Promise<string> | null = null;

export const getCsrfToken = async (): Promise<string> => {
  if (cachedToken) {
    return cachedToken;
  }
  if (!inFlight) {
    inFlight = axios
      .get<Token>(`${CONFIG.HOST}/api/v1/csrf`)
      .then(res => {
        cachedToken = res.data.token;
        return cachedToken;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

/** Drop the cached token so the next mutating request fetches a fresh one. */
export const invalidateCsrfToken = (): void => {
  cachedToken = null;
  inFlight = null;
};
